import { SearchV2 } from '@cimplico/components';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { useCalculations } from '../../../queries/use-calculations';
import { useCustomWorksheetTemplates } from '../../../queries/use-custom-worksheet-templates';
import { Worksheet, WorksheetTypeEnum } from '../../../types/worksheet';
import TemplateCard from './TemplateCard';

type TemplateBrowserProps = {
    worksheet: Worksheet;
    onBackClick?: () => void;
};

function TemplateBrowser({ worksheet, onBackClick }: TemplateBrowserProps): JSX.Element {
    const { entity } = useWorkpaperContext();
    const [search, setSearch] = useState('');
    const templatesQuery = useCustomWorksheetTemplates({
        status: 'PUBLISHED',
        entityType: [entity.type],
        search,
        allowAsSchedule: worksheet.type === WorksheetTypeEnum.GENERIC ? true : undefined,
    });
    const calculationsQuery = useCalculations();

    // List of templates that this worksheet doesn't already utilise
    const templates = (templatesQuery.data?.templates ?? []).filter((t) => !worksheet.calculations.find((c) => c.templateId === t.id));
    const calculations = calculationsQuery.data?.calculations ?? [];

    // Suggested template based on matching report code / tag
    const suggestedTemplates = templates.filter((template) => template.tags.includes(worksheet.tag ?? ''));

    // Templates already used in this workpaper but not a suggested (shared only)
    const inWorkpaper = templates
        .filter((template) => calculations.some((c) => c.templateId === template.id && c.behaviour === 'SHARE'))
        .filter((t) => !suggestedTemplates.find((st) => st.id === t.id));

    // Any remaining applicable templates
    const remaining = templates.filter(
        (template) => !inWorkpaper.find((t) => t.id === template.id) && !suggestedTemplates.find((st) => st.id === template.id)
    );

    return (
        <div>
            <button className="text-sm text-gray-600 flex items-center mb-4 w-fit" onClick={onBackClick}>
                <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to calculations
            </button>

            <div className="grid gap-4 grid-cols-[repeat(auto-fill,300px)]">
                {templatesQuery.isLoading || templatesQuery.isFetching ? (
                    [...Array(10).keys()].map((_, i) => {
                        return <div key={i} className="animate-pulse bg-gray-200 h-[125px] rounded"></div>;
                    })
                ) : (
                    <>
                        <div className="col-span-full">
                            <SearchV2 onChange={setSearch} placeholder="Find a calculation by its name..." />
                        </div>
                        <p className="text-lg text-gray-800 font-bold col-span-full">Suggested ({suggestedTemplates.length})</p>
                        {suggestedTemplates.map((template) => (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                isSuggestion
                                worksheet={worksheet}
                                calculation={calculations.find((c) => c.templateId === template.id)}
                            />
                        ))}
                        <p className="text-lg text-gray-800 font-bold col-span-full">In this workpaper ({inWorkpaper.length})</p>
                        {inWorkpaper.map((template) => (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                worksheet={worksheet}
                                calculation={calculations.find((c) => c.templateId === template.id)}
                            />
                        ))}
                        <p className="text-lg text-gray-800 font-bold col-span-full">Available templates ({remaining.length})</p>
                        {remaining.map((template) => (
                            <TemplateCard key={template.id} template={template} worksheet={worksheet} />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

export default TemplateBrowser;
